/** @format */

import React, { useState } from "react";
import { Container } from "@mui/material";
import {
  GppGood,
  WarningAmber,
  PersonOff,
  AccessTime,
  Balance,
  AccountBalance,
  Groups,
  Add,
  CheckCircleOutline,
  ArrowForward,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import {
  SectionWrapper,
  BgGrid,
  BgOrb,
  HeaderBlock,
  Badge,
  MainHeading,
  Accent,
  SubHeading,
  ContentGrid,
  AccordionCard,
  AccordionHeader,
  IconBox,
  QuestionText,
  ChevronBox,
  AccordionBody,
  Divider,
  AnswerText,
  TrustTag,
  CTAStrip,
  CTALeft,
  CTATitle,
  CTASub,
  CTAButtons,
  PrimaryBtn,
  SecondaryBtn,
} from "./styles";

// ─── Component ────────────────────────────────────────────────────────────────

const TrustQuestions = () => {
  const { t } = useTranslation("home");
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const QA = [
    { icon: <GppGood />, question: t("trustQuestions.q1Question"), answer: t("trustQuestions.q1Answer"), tag: t("trustQuestions.q1Tag") },
    { icon: <WarningAmber />, question: t("trustQuestions.q2Question"), answer: t("trustQuestions.q2Answer"), tag: t("trustQuestions.q2Tag") },
    { icon: <PersonOff />, question: t("trustQuestions.q3Question"), answer: t("trustQuestions.q3Answer"), tag: t("trustQuestions.q3Tag") },
    { icon: <AccessTime />, question: t("trustQuestions.q4Question"), answer: t("trustQuestions.q4Answer"), tag: t("trustQuestions.q4Tag") },
    { icon: <Balance />, question: t("trustQuestions.q5Question"), answer: t("trustQuestions.q5Answer"), tag: t("trustQuestions.q5Tag") },
    { icon: <AccountBalance />, question: t("trustQuestions.q6Question"), answer: t("trustQuestions.q6Answer"), tag: t("trustQuestions.q6Tag") },
    { icon: <Groups />, question: t("trustQuestions.q7Question"), answer: t("trustQuestions.q7Answer"), tag: t("trustQuestions.q7Tag") },
  ];

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  // Split into two columns
  const leftColumn = QA.filter((_, i) => i % 2 === 0);
  const rightColumn = QA.filter((_, i) => i % 2 !== 0);

  const renderItem = (item: (typeof QA)[0], globalIndex: number) => {
    const isOpen = openIndex === globalIndex;
    return (
      <AccordionCard
        key={globalIndex}
        isopen={String(isOpen)}
        onClick={() => toggle(globalIndex)}
        data-aos="fade-up"
        data-aos-duration="700"
        data-aos-delay={globalIndex * 60}
      >
        <AccordionHeader>
          <IconBox isopen={String(isOpen)}>{item.icon}</IconBox>

          <QuestionText isopen={String(isOpen)}>{item.question}</QuestionText>

          <ChevronBox isopen={String(isOpen)}>
            <Add />
          </ChevronBox>
        </AccordionHeader>

        {isOpen && (
          <AccordionBody>
            <Divider />
            <AnswerText>{item.answer}</AnswerText>
            <div style={{ marginTop: "14px" }}>
              <TrustTag>
                <CheckCircleOutline style={{ fontSize: "13px" }} />
                {item.tag}
              </TrustTag>
            </div>
          </AccordionBody>
        )}
      </AccordionCard>
    );
  };

  return (
    <SectionWrapper>
      {/* Background decorations */}
      <BgGrid />
      <BgOrb
        size={400}
        sx={{
          top: "-80px",
          right: "-100px",
          background:
            "radial-gradient(circle, rgba(30,193,149,0.08), transparent 70%)",
          animationDelay: "0s",
        }}
      />
      <BgOrb
        size={320}
        sx={{
          bottom: "60px",
          left: "-80px",
          background:
            "radial-gradient(circle, rgba(4,13,69,0.06), transparent 70%)",
          animationDelay: "6s",
        }}
      />

      <Container maxWidth="xl">
        {/* Header */}
        <HeaderBlock data-aos="fade-up">
          <Badge>
            <GppGood style={{ fontSize: "15px" }} />
            {t("trustQuestions.badge")}
          </Badge>

          <MainHeading variant="h2">
            {t("trustQuestions.heading")} <Accent>{t("trustQuestions.headingAccent")}</Accent>
          </MainHeading>

          <SubHeading>
            {t("trustQuestions.subheading")}
          </SubHeading>
        </HeaderBlock>

        {/* Two-column accordion grid */}
        <ContentGrid>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {leftColumn.map((item, colIdx) =>
              renderItem(item, colIdx * 2)
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {rightColumn.map((item, colIdx) =>
              renderItem(item, colIdx * 2 + 1)
            )}
          </div>
        </ContentGrid>

        {/* CTA strip */}
        <CTAStrip data-aos="fade-up" data-aos-delay="200">
          <CTALeft>
            <CTATitle>{t("trustQuestions.ctaTitle")}</CTATitle>
            <CTASub>{t("trustQuestions.ctaSub")}</CTASub>
          </CTALeft>

          <CTAButtons>
            <PrimaryBtn href="/circle/sign-up">
              {t("trustQuestions.ctaPrimary")}
              <ArrowForward style={{ fontSize: "16px" }} />
            </PrimaryBtn>
            <SecondaryBtn href="/faq">
              {t("trustQuestions.ctaSecondary")}
            </SecondaryBtn>
          </CTAButtons>
        </CTAStrip>
      </Container>
    </SectionWrapper>
  );
};

export default TrustQuestions;
